import { useApolloClient } from "@apollo/client";
import { getDataOrNull, omitDeep } from "@msys/common";
import { getFormattedFloat } from "@msys/formatting";
import {
  CollapseSection,
  LabeledValue,
  ModalContent,
  ModalDialog,
  ModalTitle,
} from "@msys/ui";
import InfoIcon from "@mui/icons-material/InfoOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { capitalize, isArray, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  AskWhen,
  AskWhom,
  DefineItemPropsOverridesEntryInput,
  EnterItemProps2ValueEntry,
  Props2,
} from "../../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { FormikError } from "../../../commons/form-fields/FormikError.js";
import { Attachment } from "../../attachments/helpers.js";
import { AllowedValuesExcludeFormWithOverride } from "../AllowedValuesExcludeFormWithOverride.js";
import { AllowedValuesMediaFormWithOverride } from "../AllowedValuesMediaFormWithOverride.js";
import {
  getValuesMediaBool,
  ValueMediaBool,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import { ItemPropsOverrideFragment } from "../properties.generated.js";
import {
  AllowedValue,
  applyPropertyOverrides,
  getAllowedValues,
  getPropertyType,
  getPropValue,
  isArrayType,
  isComputedProp,
  isNonComputedProp,
  isNumberType,
  isTextType,
  PropType,
} from "../properties.js";
import { PropertyField } from "../PropertyField.js";
import { QuestionControlFormWithOverride } from "../QuestionControlFormWithOverride.js";
import {
  ItemPropsCustomizeOverrideModal_ItemFragment,
  useItemPropsCustomizeOverrideModalQuery,
} from "./ItemPropsCustomizeOverrideModal.generated.js";

export interface FormValues {
  questionControl: Array<{ askWhom: AskWhom[]; askWhen: AskWhen[] }> | null;
  defaultValue: string | string[] | number | number[] | null | undefined;
  clientVisibility: boolean | null;
  allowedValuesExclude: string[] | number[];
  allowedValuesMedia: {
    valueAsString: string;
    media: Attachment | null;
  }[];
}

export const defaultFormValues: FormValues = {
  questionControl: null,
  defaultValue: undefined,
  clientVisibility: null,
  allowedValuesExclude: [],
  allowedValuesMedia: [],
};

export function overrideToFormValues(
  override: ItemPropsOverrideFragment | null,
  propertyType: PropType
): FormValues {
  return {
    defaultValue:
      override?.valueAsJson !== null && override?.valueAsJson !== undefined
        ? JSON.parse(override.valueAsJson)
        : undefined,
    clientVisibility:
      override?.clientVisibility !== null &&
      override?.clientVisibility !== undefined
        ? override.clientVisibility
        : null,
    allowedValuesExclude:
      override?.allowedValuesExclude !== null &&
      override?.allowedValuesExclude !== undefined
        ? propertyType === "NUMBER"
          ? override.allowedValuesExclude.map(v => parseFloat(v))
          : override.allowedValuesExclude
        : [],
    allowedValuesMedia:
      override?.allowedValuesMedia
        .map(avm => omitDeep(avm, "__typename"))
        .map(avm => ({
          ...avm,
          media: avm.media ?? null,
        })) ?? [],
    questionControl:
      override?.questionControl !== undefined &&
      override?.questionControl !== null
        ? omitDeep(override.questionControl, "__typename")
        : null,
  };
}

export function createAtLeastOneShouldBeSelectedTest(prop: Props2) {
  const propAllowedValues = getAllowedValues(prop);
  return function (values: any[] | undefined): boolean {
    if (!values || !propAllowedValues.length) return true;
    const allowedValues = propAllowedValues.filter(
      propAllowedValue => !values.includes(propAllowedValue.value)
    );
    return allowedValues.length > 0;
  };
}

export function createShouldBeInAllowedValuesTest(prop: Props2) {
  const propAllowedValues = getAllowedValues(prop);
  return function (
    this: Yup.TestContext,
    value: string | string[] | number | number[] | boolean | null | undefined
  ): boolean {
    if (!propAllowedValues.length) return true;
    // @ts-ignore
    const valuesExclude: any[] = this.parent.allowedValuesExclude ?? [];
    if (!valuesExclude.length) return true;
    const effectiveValue = value !== undefined ? value : getPropValue(prop);
    if (
      effectiveValue === null ||
      effectiveValue === undefined ||
      (isArray(effectiveValue) && !effectiveValue.length)
    )
      return true;
    const effectiveAllowedValues = propAllowedValues
      .filter(av => !valuesExclude.includes(av.value as never))
      .map(av => av.value);
    if (
      isNonComputedProp(prop) &&
      (isTextType(prop) || isNumberType(prop)) &&
      !isArrayType(prop)
    ) {
      return effectiveAllowedValues.includes(effectiveValue as string | number);
    }
    if (
      isNonComputedProp(prop) &&
      (isTextType(prop) || isNumberType(prop)) &&
      isArrayType(prop)
    ) {
      return (effectiveValue as (string | number)[]).every(dv =>
        effectiveAllowedValues.includes(dv)
      );
    }
    return true;
  };
}

interface Props {
  templateId: string;
  itemId: string;
  versionNumber: number;
  override: ItemPropsOverrideFragment | null;
  propertyKey: string;
  handleSave: (
    newOverrides: DefineItemPropsOverridesEntryInput | null,
    newValues: FormValues
  ) => Promise<void>;
  handlePrevious: null | (() => void);
  handleNext: null | (() => void);
  handleClose: () => void;
}

export const ItemPropsCustomizeOverrideModal = (props: Props) => {
  const client = useApolloClient();
  const query = useItemPropsCustomizeOverrideModalQuery({
    client,
    variables: {
      docId: props.templateId,
      itemId: props.itemId,
      versionNumber: props.versionNumber,
      applyOverrides: false,
    },
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const item = quoteTemplate?.item?.[0];

  if (!item) return null;

  return (
    <ItemPropsCustomizeOverrideInnerModal
      key={
        props.propertyKey
      } /*important to set to recreate form after a property change within a single item*/
      item={item}
      {...props}
    />
  );
};

export function ItemPropsCustomizeOverrideInnerModal({
  templateId,
  itemId,
  item,
  override,
  propertyKey,
  handleSave,
  handlePrevious,
  handleNext,
  handleClose,
  initialValues: initialValuesProp,
}: Props & {
  item: ItemPropsCustomizeOverrideModal_ItemFragment;
  initialValues?: FormValues;
}) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const property = item.props2.find(p => p.key === propertyKey);
  if (!property) throw new Error("Not found property");
  if (isComputedProp(property))
    throw new Error("Cannot customize computed property");

  const propertyType = getPropertyType(property);

  const initialValues: FormValues = React.useMemo(
    () => initialValuesProp ?? overrideToFormValues(override, propertyType),
    [initialValuesProp, override, propertyType]
  );

  const propAllowedValues: AllowedValue[] = React.useMemo(
    () => getAllowedValues(property),
    [property]
  );

  const propValuesMediaBool: ValueMediaBool[] = React.useMemo(
    () => getValuesMediaBool(property),
    [property]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        defaultValue: Yup.mixed()
          .nullable()
          .optional()
          .label(t("Default value", { ns: "QuoteItem" }))
          .test(
            "shouldBeInAllowedValues",
            t("Not in allowed values", { ns: "QuoteItem" }),
            createShouldBeInAllowedValuesTest(property)
          ),
        allowedValuesExclude: Yup.array(Yup.mixed()).test(
          "atLeastOneShouldBeSelected",
          t("At least one value is required", {
            ns: "Global",
          }),
          createAtLeastOneShouldBeSelectedTest(property)
        ),
        questionControl: Yup.array(
          Yup.object().shape({
            askWhen: Yup.array(Yup.string()),
            askWhom: Yup.array(Yup.string()),
          })
        ).nullable(),
        clientVisibility: Yup.boolean().nullable(),
        allowedValuesMedia: Yup.array(
          Yup.object().shape({
            valueAsString: Yup.string(),
            media: Yup.mixed().nullable(),
          })
        ),
      }),
    [property, t]
  );

  const handleSubmit = async (values: FormValues) => {
    await handleSave(
      !hasPropValuesOverride(values)
        ? null
        : overrideValuesToInput(propertyKey, values),
      { ...defaultFormValues, ...values }
    );
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <ModalDialog handleClose={handleClose}>
          <ModalTitle
            title={t("Customise property", { ns: "QuoteItem" })}
            headerButtons={
              <Tooltip
                title={
                  <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                    {t(
                      "Here you can customise the property so that it fits best to your organisation. A revert button is available next to each setting, so you can revert to the original template version, your changes will be lost. Do not forget to save your changes when you are done. You can move to configure the the next or previous property with the buttons at the bottom of the screen.",
                      { ns: "QuoteItem" }
                    )}
                  </Box>
                }
              >
                <InfoIcon />
              </Tooltip>
            }
            handleClose={handleClose}
          />
          <ModalContent>
            <Form id={formId}>
              <Stack direction={"column"} spacing={2}>
                <CollapseSection
                  title={
                    <Stack
                      component={"span"}
                      direction={"row"}
                      spacing={1}
                      alignItems="center"
                    >
                      <span>
                        {t("Property information", { ns: "QuoteItem" })}
                      </span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "In the section Property information you can see some aspects of the property which can't be customised. They are available as reference in order to better understand the context of the configurations.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Stack spacing={1}>
                    <LabeledValue label={t("Name", { ns: "Global" })}>
                      {property.label}
                    </LabeledValue>
                    <LabeledValue label={t("Type", { ns: "QuoteItem" })}>
                      {capitalize(getPropertyType(property))}
                    </LabeledValue>
                    <LabeledValue label={t("Prompt", { ns: "QuoteItem" })}>
                      {property.prompt || "–"}
                    </LabeledValue>
                    <LabeledValue
                      label={t("Allow multiple", { ns: "QuoteItem" })}
                    >
                      {isArrayType(property)
                        ? t("Yes", { ns: "Global" })
                        : t("No", { ns: "Global" })}
                    </LabeledValue>
                  </Stack>
                </CollapseSection>

                <CollapseSection
                  title={
                    <Stack
                      component={"span"}
                      direction={"row"}
                      spacing={1}
                      alignItems="center"
                    >
                      <span>{t("Property values", { ns: "QuoteItem" })}</span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "If the property has a list of allowed values, you can see them here. If you deselect some values they will be excluded as options for this template. Values that are deselected can not be set for the property in the quote and will not be available to wizard users for selection. You can edit the picture that should be shown on the wizard for each value. You can define a default for the property so that it will be preselected in the quote as an initial value.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Stack spacing={2}>
                    <AllowedValuesExcludeFormWithOverride
                      propAllowedValues={propAllowedValues}
                      propertyType={propertyType}
                      property={property}
                    />

                    <AllowedValuesMediaFormWithOverride
                      propValuesMediaBool={propValuesMediaBool}
                      propertyType={propertyType}
                    />

                    {(() => {
                      const newProperty = applyPropertyOverrides(
                        property,
                        formikProps.values.defaultValue,
                        formikProps.values.allowedValuesExclude
                      );
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Stack direction="column" spacing={1} flex={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Box display="flex" flex={1}>
                                <PropertyField
                                  label={t("Default value", {
                                    ns: "QuoteItem",
                                  })}
                                  projectId={null}
                                  docId={templateId}
                                  itemId={itemId}
                                  property={newProperty}
                                  setPropertyValue={async entryValue => {
                                    await formikProps.setFieldValue(
                                      "defaultValue",
                                      getEntryValue(entryValue)
                                    );
                                    await formikProps.setFieldTouched(
                                      "defaultValue",
                                      true
                                    );
                                  }}
                                  disableExpressions
                                />
                              </Box>
                              <PropertyNumberSliderField
                                property={newProperty}
                                onChange={async value => {
                                  await formikProps.setFieldValue(
                                    "defaultValue",
                                    value
                                  );
                                  await formikProps.setFieldTouched(
                                    "defaultValue",
                                    true
                                  );
                                }}
                              />
                            </Stack>
                            <FormikError
                              formikProps={formikProps}
                              name="defaultValue"
                            />
                          </Stack>
                          <IconButton
                            color="primary"
                            size="small"
                            sx={{ flexGrow: 0, flexShrink: 0 }}
                            disabled={
                              formikProps.values.defaultValue === undefined
                            }
                            onClick={async () => {
                              await formikProps.setFieldValue(
                                "defaultValue",
                                undefined
                              );
                              await formikProps.setFieldTouched(
                                "defaultValue",
                                true
                              );
                            }}
                          >
                            <Tooltip title={t("Reset", { ns: "Global" })}>
                              <RestartAltIcon />
                            </Tooltip>
                          </IconButton>
                        </Stack>
                      );
                    })()}
                  </Stack>
                </CollapseSection>

                <CollapseSection
                  title={
                    <Stack
                      component={"span"}
                      direction={"row"}
                      spacing={1}
                      alignItems="center"
                    >
                      <span>
                        {t("Property visibility", { ns: "QuoteItem" })}
                      </span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "The property visibility determines if a property will be shown on the quote document or not. If the visibility is selected, the property will be shown on the quote. If the visibility is not selected, the property will remain hidden. Regardless of this setting, you can still ask the property in the wizard if you want, the visibility will have no impact on it.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckboxField
                      name="clientVisibility"
                      label={t("Visibility", { ns: "Global" })}
                      value={
                        formikProps.values.clientVisibility !== null
                          ? formikProps.values.clientVisibility
                          : property.clientVisibility
                      }
                      fullWidth
                    />

                    <IconButton
                      color="primary"
                      size="small"
                      sx={{ flexGrow: 0, flexShrink: 0 }}
                      disabled={formikProps.values.clientVisibility === null}
                      onClick={async () => {
                        await formikProps.setFieldValue(
                          "clientVisibility",
                          null
                        );
                      }}
                    >
                      <Tooltip title={t("Reset", { ns: "Global" })}>
                        <RestartAltIcon />
                      </Tooltip>
                    </IconButton>
                  </Stack>
                </CollapseSection>

                <CollapseSection
                  title={
                    <Stack
                      component={"span"}
                      direction={"row"}
                      spacing={1}
                      alignItems="center"
                    >
                      <span>{t("Question control", { ns: "QuoteItem" })}</span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "The question control determines in which wizard a question is shown and to whom. You can set up additional configurations and remove existing ones.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                  isInitiallyExpanded={true}
                  ActionButtons={
                    <IconButton
                      color="primary"
                      size="small"
                      sx={{ flexGrow: 0, flexShrink: 0 }}
                      disabled={formikProps.values.questionControl === null}
                      onClick={async () => {
                        await formikProps.setFieldValue(
                          "questionControl",
                          null
                        );
                      }}
                    >
                      <Tooltip title={t("Reset", { ns: "Global" })}>
                        <RestartAltIcon />
                      </Tooltip>
                    </IconButton>
                  }
                >
                  <QuestionControlFormWithOverride property={property} />
                </CollapseSection>
              </Stack>
            </Form>
          </ModalContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button
              onClick={handlePrevious ?? undefined}
              disabled={formikProps.isSubmitting || formikProps.dirty}
              sx={{ visibility: handlePrevious ? "visible" : "hidden" }}
            >
              {t("Previous", { ns: "Global" })}
            </Button>
            <Stack direction={"row"} spacing={1}>
              <Button variant="text" color="secondary" onClick={handleClose}>
                {t("Close", { ns: "Global" })}
              </Button>
              <Button
                type="submit"
                form={formId}
                variant="contained"
                color="primary"
                loading={formikProps.isSubmitting}
                disabled={!formikProps.isValid || !formikProps.dirty}
              >
                {t("Save", {
                  ns: "Global",
                })}
              </Button>
            </Stack>
            <Button
              onClick={handleNext ?? undefined}
              disabled={formikProps.isSubmitting || formikProps.dirty}
              sx={{ visibility: handleNext ? "visible" : "hidden" }}
            >
              {t("Next", { ns: "Global" })}
            </Button>
          </DialogActions>
        </ModalDialog>
      )}
    </Formik>
  );
}

function PropertyNumberSliderField({
  property,
  onChange,
}: {
  property: Props2;
  onChange: (number: number) => Promise<void>;
}) {
  const language = useTolgee(["language"]).getLanguage()!;
  return isNonComputedProp(property) &&
    isNumberType(property) &&
    !isArrayType(property) &&
    !property.allowedValuesNumber.length &&
    property.range?.min !== null &&
    property.range?.min !== undefined &&
    property.range?.max !== null &&
    property.range?.max !== undefined ? (
    <Stack direction="row" spacing={3} pt={1} alignItems="center" flex={1}>
      <Box pl={2}>
        <Typography variant="body2">
          {getFormattedFloat(
            property.range.min,
            language,
            property.step ?? undefined
          )}
        </Typography>
      </Box>
      <Slider
        color="secondary"
        value={(getPropValue(property) as number | null) ?? property.range.min}
        onChange={async (event, newValue) => {
          if (typeof newValue === "number") {
            await onChange(newValue);
          }
        }}
        valueLabelDisplay="auto"
        min={property.range.min}
        max={property.range.max}
        step={property.step ?? 0.01}
        sx={{ minWidth: "80px" }}
      />
      <Box pr={2}>
        <Typography variant="body2">
          {getFormattedFloat(
            property.range.max,
            language,
            property.step ?? undefined
          )}
        </Typography>
      </Box>
    </Stack>
  ) : null;
}

export const overrideValuesToInput = (
  propertyKey: string,
  values: FormValues
): DefineItemPropsOverridesEntryInput => {
  return {
    key: propertyKey,
    questionControl: values.questionControl,
    clientVisibility: values.clientVisibility,
    allowedValuesExclude: values.allowedValuesExclude.map(v => v.toString()),
    allowedValuesMedia: values.allowedValuesMedia.map(avm =>
      omitDeep(avm, "id", "__typename")
    ),
    valueAsJson:
      values.defaultValue !== undefined
        ? JSON.stringify(values.defaultValue)
        : undefined,
  };
};

export function hasPropValuesOverride(values: FormValues) {
  return (
    values.defaultValue !== undefined ||
    values.questionControl !== null ||
    values.clientVisibility !== null ||
    values.allowedValuesExclude.length > 0 ||
    values.allowedValuesMedia.length > 0
  );
}

export function getEntryValue(
  value: EnterItemProps2ValueEntry
): string | number | string[] | number[] | boolean | null {
  if (value.bool) return value.bool.valueBool ?? null;
  if (value.text) return value.text.valueText ?? null;
  if (value.textArray) return value.textArray.valueTextArray ?? null;
  if (value.number) return value.number.valueNumber ?? null;
  if (value.numberArray) return value.numberArray.valueNumberArray ?? null;
  throw new Error("Wrong value entry provided");
}
